import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDeleteOrganization } from '@/graphql/mutations/organizations';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui-kit/alert-dialog';
import { Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import type { Organization } from '../../types/org.types';

interface DangerZoneSettingsProps {
  org: Organization;
}

export const DangerZoneSettings = ({ org }: DangerZoneSettingsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deleteOrg = useDeleteOrganization();
  const [confirmText, setConfirmText] = useState('');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (confirmText !== org.Name) return;

    setError(null);

    try {
      await deleteOrg.mutateAsync({ itemId: org.ItemId, isHardDelete: true });
      navigate('/');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('ORGSETTINGS_DANGERZONE_ERRORS_DELETEFAILED')
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="flex items-start gap-4 p-4 rounded-lg border border-crimson/30 bg-crimson/5">
        <AlertTriangle className="h-6 w-6 text-crimson shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-crimson">{t('ORGSETTINGS_DANGERZONE_WARNING_TITLE')}</p>
          <p className="text-sm text-globe-grey mt-1">
            {t('ORGSETTINGS_DANGERZONE_WARNING_DESCRIPTION')}
          </p>
        </div>
      </div>

      {/* Delete Organization Card */}
      <Card className="border-crimson/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-crimson" />
            {t('ORGSETTINGS_DANGERZONE_DELETECARD_TITLE')}
          </CardTitle>
          <CardDescription>{t('ORGSETTINGS_DANGERZONE_DELETECARD_DESCRIPTION')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-sm text-globe-grey space-y-2">
              <p>{t('ORGSETTINGS_DANGERZONE_DELETELIST_INTRO')}</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>{t('ORGSETTINGS_DANGERZONE_DELETELIST_TEMPLATES')}</li>
                <li>{t('ORGSETTINGS_DANGERZONE_DELETELIST_TEAMMEMBERS')}</li>
                <li>{t('ORGSETTINGS_DANGERZONE_DELETELIST_INVITATIONS')}</li>
                <li>{t('ORGSETTINGS_DANGERZONE_DELETELIST_LETTERHEADS')}</li>
                <li>{t('ORGSETTINGS_DANGERZONE_DELETELIST_APICREDENTIALS')}</li>
              </ul>
            </div>
            <div className="flex justify-center items-center gap-2">
              <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size={'sm'} className="gap-2">
                    <Trash2 className="h-4 w-4" />
                    {t('ORGSETTINGS_DANGERZONE_ACTIONS_DELETEORGANIZATION')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-crimson">
                      <AlertTriangle className="h-5 w-5" />
                      {t('ORGSETTINGS_DANGERZONE_DIALOG_TITLE', { name: org.Name })}
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-4">
                        <p>{t('ORGSETTINGS_DANGERZONE_DIALOG_DESCRIPTION')}</p>
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-[hsl(var(--fg))]">
                            {t('ORGSETTINGS_DANGERZONE_DIALOG_CONFIRMLABEL', {
                              name: org.Name,
                            })}
                          </p>
                          <Input
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={org.Name}
                            className="font-mono"
                          />
                        </div>
                        {error && <p className="text-crimson text-sm">{error}</p>}
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setConfirmText('')}>
                      {t('ORGSETTINGS_DANGERZONE_ACTIONS_CANCEL')}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      disabled={confirmText !== org.Name || deleteOrg.isPending}
                      className="bg-crimson hover:bg-crimson/90"
                    >
                      {deleteOrg.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {t('ORGSETTINGS_DANGERZONE_ACTIONS_DELETEFOREVER')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
